(() => {
  const parseJson = (element, selector) => {
    try {
      return JSON.parse(element.querySelector(selector)?.textContent || '{}');
    } catch (error) {
      console.error('Lane catalog metadata could not be parsed.', error);
      return {};
    }
  };

  const subscribeToVariantChanges = (element, callback) => {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return () => {};

    return subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      if (!data?.variant || String(data.sectionId) !== String(element.dataset.sectionId)) return;
      callback(String(data.variant.id));
    });
  };

  const youtubeEmbedUrl = (value) => {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      let videoId = '';

      if (url.hostname === 'youtu.be') videoId = url.pathname.split('/').filter(Boolean)[0] || '';
      if (url.hostname.includes('youtube.com')) {
        videoId = url.searchParams.get('v') || url.pathname.split('/embed/')[1]?.split('/')[0] || '';
      }

      return videoId ? `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?rel=0` : '';
    } catch {
      return '';
    }
  };

  class LaneProductAudio extends HTMLElement {
    connectedCallback() {
      this.media = parseJson(this, '[data-lane-audio-map]');
      this.audio = this.querySelector('audio');
      // Deter native Save audio as without restricting the rest of the page.
      this.preventAudioContextMenu = (event) => event.preventDefault();
      this.audio?.addEventListener('contextmenu', this.preventAudioContextMenu);
      this.panel = this.querySelector('[data-lane-audio-panel]');
      this.credit = this.querySelector('[data-lane-audio-credit]');
      this.voicing = this.querySelector('[data-lane-audio-voicing]');
      this.performer = this.querySelector('[data-lane-audio-performer]');
      this.unsubscribe = subscribeToVariantChanges(this, (variantId) => this.update(variantId));
      this.update(this.dataset.variantId);
    }

    disconnectedCallback() {
      this.audio?.removeEventListener('contextmenu', this.preventAudioContextMenu);
      this.unsubscribe?.();
    }

    update(variantId) {
      if (!this.audio || !this.panel) return;

      const variant = this.media.variants?.[variantId] || {};
      const nextUrl = variant.url || this.media.default || '';
      const inferredVoicing =
        variant.option && variant.option !== 'Default Title' && (variant.url || this.media.singleVoicing)
          ? variant.option
          : '';
      const nextVoicing = variant.voicing || this.media.defaultVoicing || inferredVoicing;
      const nextPerformer = variant.performer || this.media.defaultPerformer || '';
      const currentUrl = this.audio.getAttribute('src') || '';

      this.panel.hidden = !nextUrl;
      if (this.voicing) {
        this.voicing.textContent = nextVoicing ? `Recording: ${nextVoicing}` : '';
        this.voicing.hidden = !nextVoicing;
      }
      if (this.performer) {
        this.performer.textContent = nextPerformer
          ? this.performer.dataset.lanePerformerTemplate.replace('[performer]', nextPerformer)
          : '';
        this.performer.hidden = !nextUrl || !nextPerformer;
      }
      if (this.credit) this.credit.hidden = !nextVoicing && !nextPerformer;
      if (!nextUrl || nextUrl === currentUrl) return;

      this.audio.pause();
      this.audio.setAttribute('src', nextUrl);
      this.audio.load();
    }
  }

  class LaneProductVideo extends HTMLElement {
    connectedCallback() {
      this.media = parseJson(this, '[data-lane-video-map]');
      this.panel = this.querySelector('[data-lane-video-panel]');
      this.frame = this.querySelector('iframe');
      this.unsubscribe = subscribeToVariantChanges(this, (variantId) => this.update(variantId));
      this.update(this.dataset.variantId);
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    update(variantId) {
      if (!this.panel || !this.frame) return;

      const videoUrl = this.media.variants?.[variantId] || this.media.default || '';
      const embedUrl = youtubeEmbedUrl(videoUrl);
      this.panel.hidden = !embedUrl;

      if (!embedUrl || this.frame.getAttribute('src') === embedUrl) return;
      this.frame.setAttribute('src', embedUrl);
    }
  }

  class LaneExternalPurchase extends HTMLElement {
    connectedCallback() {
      this.urls = parseJson(this, '[data-lane-external-map]');
      this.link = this.querySelector('[data-lane-external-link]');
      this.unsubscribe = subscribeToVariantChanges(this, (variantId) => this.update(variantId));
      this.update(this.dataset.variantId);
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    update(variantId) {
      const url = this.urls.variants?.[variantId] || this.urls.default || '';
      this.hidden = !url;

      if (this.link && url) this.link.href = url;

      if (this.dataset.toggleShopifyPurchase === 'true') {
        this.closest('product-info')
          ?.querySelectorAll('[data-lane-shopify-purchase], [data-lane-product-price]')
          .forEach((element) => {
            element.hidden = Boolean(url);
          });
      }
    }
  }

  class LaneCatalogDirectory extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-lane-catalog-search]');
      this.cards = Array.from(this.querySelectorAll('[data-lane-catalog-card]'));
      this.letters = Array.from(this.querySelectorAll('[data-lane-catalog-letter]'));
      this.count = this.querySelector('[data-lane-catalog-count]');
      this.empty = this.querySelector('[data-lane-catalog-empty]');

      this.input?.addEventListener('input', () => this.filter());
      this.filter();
    }

    filter() {
      const query = this.input?.value.trim().toLocaleLowerCase() || '';
      let visibleCount = 0;

      this.cards.forEach((card) => {
        const visible = !query || card.dataset.searchText?.includes(query);
        card.hidden = !visible;
        if (visible) visibleCount += 1;
      });

      this.letters.forEach((letter) => {
        const letterValue = letter.dataset.laneCatalogLetter;
        letter.hidden = !this.cards.some(
          (card) => !card.hidden && card.dataset.letter === letterValue
        );
      });

      if (this.count) this.count.textContent = `${visibleCount} ${visibleCount === 1 ? 'song' : 'songs'}`;
      if (this.empty) this.empty.hidden = visibleCount !== 0;
    }
  }

  const wireCompatibleVariantSelection = () => {
    const getGroups = (variantSelects) =>
      Array.from(variantSelects.querySelectorAll('fieldset, .product-form__input--dropdown'));

    const getSelectedValues = (groups) =>
      groups.map((group) => {
        const selected = group.querySelector('input:checked') || group.querySelector('select');
        return selected?.value || '';
      });

    const isPurchasable = (variant) => variant.external || (variant.available && !variant.deliveryBlocked);

    const setGroupValue = (group, expectedValue) => {
      const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
        (input) => input.value === expectedValue
      );
      if (radio) {
        radio.checked = true;
        return;
      }

      const select = group.querySelector('select');
      const option = Array.from(select?.options || []).find((item) => item.value === expectedValue);
      if (select && option) {
        Array.from(select.options).forEach((item) => item.removeAttribute('selected'));
        select.value = expectedValue;
        option.setAttribute('selected', 'selected');
      }
    };

    const updateAvailability = (variantSelects, variants, groups) => {
      const selectedValues = getSelectedValues(groups);
      groups.forEach((group, groupIndex) => {
        const controls = group.matches('.product-form__input--dropdown')
          ? Array.from(group.querySelectorAll('option'))
          : Array.from(group.querySelectorAll('input[type="radio"]'));

        controls.forEach((control) => {
          const candidateValue = control.value;
          const possible = variants.some((variant) => {
            if (!isPurchasable(variant) || variant.options[groupIndex] !== candidateValue) return false;
            return variant.options.every((value, index) => {
              if (index >= groupIndex) return true;
              return value === selectedValues[index];
            });
          });

          if (control.tagName === 'OPTION') {
            control.disabled = !possible;
          } else {
            control.disabled = !possible;
            control.classList.toggle('disabled', !possible);
            control.setAttribute('aria-disabled', String(!possible));
            const label = variantSelects.querySelector(`label[for="${CSS.escape(control.id)}"]`);
            label?.classList.toggle('lane-option-unavailable', !possible);
          }
        });
      });
    };

    const initialize = (variantSelects) => {
      const variants = parseJson(variantSelects, '[data-lane-variant-option-map]');
      if (!Array.isArray(variants) || variants.length === 0) return;
      updateAvailability(variantSelects, variants, getGroups(variantSelects));
    };

    document.addEventListener(
      'change',
      (event) => {
        const target = event.target;
        const variantSelects = target?.closest?.('variant-selects');
        if (!variantSelects || !['INPUT', 'SELECT'].includes(target.tagName)) return;

        const variants = parseJson(variantSelects, '[data-lane-variant-option-map]');
        if (!Array.isArray(variants) || variants.length === 0) return;

        const groups = getGroups(variantSelects);
        const changedIndex = groups.findIndex((group) => group.contains(target));
        if (changedIndex < 0) return;

        const selectedValues = getSelectedValues(groups);
        const hasExactVariant = variants.some(
          (variant) =>
            isPurchasable(variant) &&
            variant.options.length === selectedValues.length &&
            variant.options.every((value, index) => value === selectedValues[index])
        );

        if (!hasExactVariant) {
          const candidates = variants
            .filter((variant) => isPurchasable(variant) && variant.options[changedIndex] === target.value)
            .map((variant) => ({
              ...variant,
              matchCount: variant.options.reduce(
                (count, value, index) =>
                  count + (index !== changedIndex && value === selectedValues[index] ? 1 : 0),
                0
              ),
            }))
            .sort((left, right) => right.matchCount - left.matchCount || Number(right.external) - Number(left.external));
          const prefersChoralDefault = changedIndex === 0 && !/solo|duet|instrument|piano|violin|flute|oboe|clarinet|cello|trumpet/i.test(target.value);
          const preferredVariant = prefersChoralDefault
            ? candidates.find((variant) => /medium choir|print 13-24 copies/i.test(variant.options[1] || ''))
            : null;
          const compatibleVariant = preferredVariant || candidates[0];
          if (!compatibleVariant) return;

          groups.forEach((group, index) => {
            if (index !== changedIndex) setGroupValue(group, compatibleVariant.options[index]);
          });

          const status = variantSelects.querySelector('[data-lane-variant-status]');
          if (status) {
            status.textContent = `Updated to the compatible option: ${compatibleVariant.options.join(', ')}.`;
          }
        }

        updateAvailability(variantSelects, variants, groups);
      },
      true
    );

    document.querySelectorAll('variant-selects').forEach(initialize);
    document.addEventListener('product-info:loaded', (event) => {
      event.target?.querySelectorAll?.('variant-selects').forEach(initialize);
    });
  };

  const wireCardNavigation = () => {
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const card = event.target?.closest?.('[data-lane-card-url]');
      if (!card || event.target.closest('a, button, input, select, textarea, label, summary, details, product-form, modal-opener')) return;

      const url = card.dataset.laneCardUrl;
      if (url) window.location.assign(url);
    });
  };

  if (typeof document !== 'undefined') {
    wireCompatibleVariantSelection();
    wireCardNavigation();
  }

  if (!customElements.get('lane-product-audio')) customElements.define('lane-product-audio', LaneProductAudio);
  if (!customElements.get('lane-product-video')) customElements.define('lane-product-video', LaneProductVideo);
  if (!customElements.get('lane-external-purchase')) {
    customElements.define('lane-external-purchase', LaneExternalPurchase);
  }
  if (!customElements.get('lane-catalog-directory')) {
    customElements.define('lane-catalog-directory', LaneCatalogDirectory);
  }
})();
